import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip } from 'chart.js';
import ApiClient from '../utils/ApiClient';
import {
    displayName, deadlineTone, DEADLINE_WARNING, TONE_CLASSES, storefrontUrl, sellerListingUrl,
    buildTimeline, timelineSummary,
} from '../utils/sellerLegal';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip);

const FIELDS = [
    'stage', 'storefrontName', 'legalName', 'legalAddress', 'classification', 'priority', 'folderPath', 'notes',
    'orderNumber', 'purchaseAsin', 'purchasePrice', 'orderedOn', 'deliveredOn',
    'noticeSentOn', 'fedexTracking', 'uspsTracking', 'noticeDeliveredOn', 'cureResult',
    'caseNumber', 'court', 'filedOn', 'hagueRequestOn', 'servedOn', 'serviceMethod', 'orderOn', 'orderType',
    'judgmentOn', 'amazonTransmittedOn', 'closedReason',
];

const addDays = (iso, n) => {
    const d = new Date(`${iso}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
};

// Draws a dashed vertical line at each marker date on the ASIN-count chart.
const markerPlugin = {
    id: 'legalMarkers',
    afterDraw(chart, _args, opts) {
        const { ctx, chartArea, scales } = chart;
        (opts.markers || []).forEach((m) => {
            const idx = chart.data.labels.indexOf(m.day);
            if (idx < 0) return;
            const x = scales.x.getPixelForValue(idx);
            ctx.save();
            ctx.strokeStyle = m.color;
            ctx.setLineDash([4, 4]);
            ctx.beginPath();
            ctx.moveTo(x, chartArea.top);
            ctx.lineTo(x, chartArea.bottom);
            ctx.stroke();
            ctx.fillStyle = m.color;
            ctx.font = '11px sans-serif';
            ctx.fillText(m.label, x + 4, chartArea.top + 12);
            ctx.restore();
        });
    },
};

function Input({ label, name, form, setForm, type = 'text' }) {
    return (
        <label className="block text-sm text-gray-700">{label}
            <input type={type} value={form[name] ?? ''} onChange={(e) => setForm({ ...form, [name]: e.target.value })}
                   className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1" />
        </label>
    );
}

function Select({ label, name, form, setForm, options, allowBlank }) {
    return (
        <label className="block text-sm text-gray-700">{label}
            <select value={form[name] ?? ''} onChange={(e) => setForm({ ...form, [name]: e.target.value || null })}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1">
                {allowBlank && <option value="">—</option>}
                {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </label>
    );
}

function Section({ title, children }) {
    return (
        <section className="mb-4 rounded-md border border-gray-200 bg-white p-4">
            <h2 className="mb-3 text-base font-semibold text-gray-900">{title}</h2>
            {children}
        </section>
    );
}

function SellerLegalDetail() {
    const { sellerId } = useParams();
    const [meta, setMeta] = useState(null);
    const [data, setData] = useState(null);
    const [form, setForm] = useState({});
    const [gates, setGates] = useState({});
    const [logNote, setLogNote] = useState('');
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);

    const apply = (d) => {
        setData(d);
        const f = {};
        FIELDS.forEach((k) => { f[k] = d.seller[k] ?? ''; });
        setForm(f);
        setGates(d.seller.gates);
    };

    const load = useCallback(async () => {
        try {
            const [m, d] = await Promise.all([
                ApiClient.get('/api/seller-legal/meta'),
                ApiClient.get(`/api/seller-legal/sellers/${sellerId}`),
            ]);
            setMeta(m.data);
            apply(d.data);
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        }
    }, [sellerId]);

    useEffect(() => { load(); }, [load]);

    // The full offer history loads on the server in the background; check back until it is done.
    const historyStatus = data?.seller?.historyStatus;
    useEffect(() => {
        if (historyStatus !== 'PENDING') return undefined;
        const timer = setInterval(async () => {
            try {
                const d = await ApiClient.get(`/api/seller-legal/sellers/${sellerId}`);
                if (d.data.seller.historyStatus !== 'PENDING') apply(d.data);
            } catch (e) {
                // keep polling; a transient error must not stop the page from updating
            }
        }, 4000);
        return () => clearInterval(timer);
    }, [historyStatus, sellerId]);

    const retryHistory = async () => {
        try {
            const res = await ApiClient.post(`/api/seller-legal/sellers/${sellerId}/history`);
            apply(res.data);
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        }
    };

    const save = async () => {
        setSaving(true);
        setError(null);
        const body = {};
        FIELDS.forEach((k) => { body[k] = form[k] === '' ? null : form[k]; });
        body.gates = Object.keys(gates).filter((g) => gates[g]);
        body.logNote = logNote || null;
        try {
            const res = await ApiClient.put(`/api/seller-legal/sellers/${sellerId}`, body);
            apply(res.data);
            setLogNote('');
        } catch (e) {
            setError(e.response?.data?.error || e.message);
        } finally {
            setSaving(false);
        }
    };

    if (error && !data) return <div className="p-6 text-red-700">{error}</div>;
    if (!data || !meta) return <div className="p-6 text-gray-500">Loading…</div>;

    const s = data.seller;
    const today = data.today;
    const markers = [];
    if (s.firstSeen) markers.push({ day: s.firstSeen, label: 'First seen', color: '#047857' });
    if (data.returnedOn) markers.push({ day: data.returnedOn, label: 'Returned', color: '#1d4ed8' });
    if (s.noticeDeliveredOn) {
        markers.push({ day: s.noticeDeliveredOn, label: 'Notice delivered', color: '#b45309' });
        markers.push({ day: addDays(s.noticeDeliveredOn, 7), label: 'Cure ends', color: '#b91c1c' });
    }
    // Extend the axis to the latest marker so a cure deadline still ahead is visible.
    const lastMarker = markers.map((m) => m.day).sort().pop();
    const timeline = buildTimeline(data.daily, lastMarker);
    const summary = timelineSummary(data.daily);
    const chart = {
        labels: timeline.labels,
        datasets: [{
            label: 'ASINs', data: timeline.values, borderColor: '#4f46e5', backgroundColor: '#4f46e5',
            tension: 0, spanGaps: false, pointRadius: timeline.labels.length > 60 ? 0 : 2, borderWidth: 1.5,
        }],
    };

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <Link to="/seller-legal-tracker" className="text-sm text-indigo-600">← Seller Legal Tracker</Link>

            <div className="mt-2 mb-4">
                <h1 className="text-2xl font-semibold text-gray-900">{displayName(s)}</h1>
                <div className="mt-1 flex flex-wrap gap-3 text-sm text-gray-600">
                    <a href={storefrontUrl(s.sellerId)} target="_blank" rel="noreferrer" className="text-indigo-600">Storefront</a>
                    <span>{meta.stages.find((x) => x.value === s.stage)?.label} · P{s.priority}</span>
                    <span>First seen {s.firstSeen ?? 'not yet'} · last seen {s.lastSeen ?? 'not yet'}</span>
                    {s.previousLastSeen && (
                        <span className="text-blue-800">Returned {data.returnedOn ?? s.firstSeen}, previously seen {s.previousLastSeen}</span>
                    )}
                </div>
            </div>

            <Section title="Deadlines and flags">
                {data.flags.map((f) => <p key={f} className="text-sm text-red-800">⚠ {f}</p>)}
                {data.deadlines.length === 0 && <p className="text-sm text-gray-500">No open deadline.</p>}
                {data.deadlines.map((d) => {
                    const days = Math.round((new Date(d.due) - new Date(today)) / 86400000);
                    return (
                        <p key={d.label} className={`text-sm ${TONE_CLASSES[deadlineTone(days)]}`}>
                            {d.label}: {d.due} <span className="text-gray-500">({d.basis})</span>
                        </p>
                    );
                })}
                <p className="mt-2 text-xs text-gray-500">{DEADLINE_WARNING}</p>
            </Section>

            <Section title="ASIN timeline">
                {s.historyStatus === 'PENDING' && (
                    <p className="mb-2 text-sm text-gray-600">Loading offer history… this runs on the server and updates here when done.</p>
                )}
                {s.historyStatus === 'FAILED' && (
                    <p className="mb-2 text-sm text-red-700">
                        Loading the offer history failed (see the activity log).{' '}
                        <button onClick={retryHistory} className="font-medium text-indigo-600 underline">Retry</button>
                    </p>
                )}
                {summary && (
                    <p className="mb-2 text-sm text-gray-700">
                        Listed on <strong>{summary.daysListed}</strong> days, {summary.from} → {summary.to} ·
                        peak <strong>{summary.peak}</strong> ASINs · latest day <strong>{summary.current}</strong>
                    </p>
                )}
                {timeline.labels.length === 0
                    ? s.historyStatus !== 'PENDING' && <p className="text-sm text-gray-500">No offers in our offer data for this seller.</p>
                    : (
                        <div className="h-48">
                            <Line data={chart} plugins={[markerPlugin]}
                                  options={{
                                      maintainAspectRatio: false,
                                      plugins: { legalMarkers: { markers }, legend: { display: false } },
                                      scales: {
                                          x: { ticks: { maxTicksLimit: 10, autoSkip: true } },
                                          y: { beginAtZero: true, ticks: { precision: 0 } },
                                      },
                                  }} />
                        </div>
                    )}
                <p className="mt-1 text-xs text-gray-500">Gaps are days the offer feed recorded nothing — not days without offers.</p>
            </Section>

            <Section title="ASINs">
                <table className="w-full text-sm">
                    <thead><tr className="text-left text-gray-500">
                        <th>ASIN</th><th>Title</th><th>Last price</th><th>First seen</th><th>Last seen</th><th>Status</th>
                    </tr></thead>
                    <tbody>
                        {data.asins.map((a) => (
                            <tr key={a.asin} className="border-t border-gray-100">
                                <td><a href={sellerListingUrl(a.asin, s.sellerId)} target="_blank" rel="noreferrer" className="text-indigo-600">{a.asin}</a></td>
                                <td className="max-w-xs truncate">{a.title}</td>
                                <td>{a.lastPrice != null ? `$${Number(a.lastPrice).toFixed(2)}` : ''}</td>
                                <td>{a.firstSeen}</td>
                                <td>{a.lastSeen}</td>
                                <td>{a.active ? 'Live' : 'Gone'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Section>

            <Section title="Stage and identity">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    <Select label="Stage" name="stage" form={form} setForm={setForm} options={meta.stages} />
                    <Select label="Priority" name="priority" form={form} setForm={setForm}
                            options={[1, 2, 3, 4, 5].map((n) => ({ value: n, label: `P${n}` }))} />
                    <Input label="Storefront name" name="storefrontName" form={form} setForm={setForm} />
                    <Input label="Legal name" name="legalName" form={form} setForm={setForm} />
                    <Input label="Legal address" name="legalAddress" form={form} setForm={setForm} />
                    <Select label="Classification" name="classification" form={form} setForm={setForm} options={meta.classifications} />
                    <Input label="Case folder" name="folderPath" form={form} setForm={setForm} />
                    <Input label="Closed reason (required to close)" name="closedReason" form={form} setForm={setForm} />
                </div>
                <label className="mt-3 block text-sm text-gray-700">Notes
                    <textarea value={form.notes ?? ''} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                              className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1" rows={3} />
                </label>
            </Section>

            <Section title="Standing purchase">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <Input label="Order number" name="orderNumber" form={form} setForm={setForm} />
                    <Input label="ASIN" name="purchaseAsin" form={form} setForm={setForm} />
                    <Input label="Price" name="purchasePrice" form={form} setForm={setForm} type="number" />
                    <Input label="Ordered on" name="orderedOn" form={form} setForm={setForm} type="date" />
                    <Input label="Delivered on" name="deliveredOn" form={form} setForm={setForm} type="date" />
                </div>
            </Section>

            <Section title="Notice and cure">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <Input label="Notice sent" name="noticeSentOn" form={form} setForm={setForm} type="date" />
                    <Input label="FedEx tracking" name="fedexTracking" form={form} setForm={setForm} />
                    <Input label="USPS tracking" name="uspsTracking" form={form} setForm={setForm} />
                    <Input label="Notice delivered" name="noticeDeliveredOn" form={form} setForm={setForm} type="date" />
                    <Select label="Cure result" name="cureResult" form={form} setForm={setForm} options={meta.cureResults} allowBlank />
                </div>
            </Section>

            <Section title="Suit">
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <Input label="Case number" name="caseNumber" form={form} setForm={setForm} />
                    <Input label="Court" name="court" form={form} setForm={setForm} />
                    <Input label="Filed" name="filedOn" form={form} setForm={setForm} type="date" />
                    <Input label="Hague request" name="hagueRequestOn" form={form} setForm={setForm} type="date" />
                    <Input label="Served" name="servedOn" form={form} setForm={setForm} type="date" />
                    <Input label="Service method" name="serviceMethod" form={form} setForm={setForm} />
                    <Input label="Order on" name="orderOn" form={form} setForm={setForm} type="date" />
                    <Input label="Order type" name="orderType" form={form} setForm={setForm} />
                    <Input label="Judgment on" name="judgmentOn" form={form} setForm={setForm} type="date" />
                    <Input label="Sent to Amazon" name="amazonTransmittedOn" form={form} setForm={setForm} type="date" />
                </div>
            </Section>

            <Section title={`Filing checklist (${Object.values(gates).filter(Boolean).length}/${meta.gates.length})`}>
                {meta.gates.map((g) => (
                    <label key={g.value} className="flex items-center gap-2 py-0.5 text-sm text-gray-800">
                        <input type="checkbox" checked={!!gates[g.value]}
                               onChange={(e) => setGates({ ...gates, [g.value]: e.target.checked })} />
                        {g.label}
                    </label>
                ))}
            </Section>

            <div className="sticky bottom-0 mb-4 flex flex-wrap items-center gap-3 border-t border-gray-200 bg-white py-3">
                <input value={logNote} onChange={(e) => setLogNote(e.target.value)} placeholder="Add a note to the activity log (optional)"
                       className="flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm" />
                <button onClick={save} disabled={saving}
                        className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50">
                    {saving ? 'Saving…' : 'Save'}
                </button>
                {error && <p className="w-full text-sm text-red-700">{error}</p>}
            </div>

            <Section title="Activity log">
                {data.events.map((e, i) => (
                    <p key={i} className="border-t border-gray-100 py-1 text-sm">
                        <span className="text-gray-500">{String(e.occurredAt).replace('T', ' ').slice(0, 16)}</span>{' '}
                        <span className="font-medium">{e.type}</span> {e.detail}
                    </p>
                ))}
            </Section>
        </div>
    );
}

export default SellerLegalDetail;
